/**
 * A list of panelist IDs attached with a brief (UAT UI-033). PURE.
 *
 * A sheet whose one column is panelist IDs is read as the list of people to
 * count (or leave out), never as brief text, the way station lists are read as
 * locations (locations.ts). A column of zip codes is not one. The model never sees the IDs: DANI used to answer
 * "the tool cannot target a specific list of panelist IDs" while the form's
 * upload counts them.
 */
import { canonicalizeRequest, FeasibilityRequestSchema, type Criterion } from '../engine/request';
import type { Sheet } from '../ooh/location-lists';
import type { BriefDraft } from '../contracts/brief';

/** The request's own limit on a panelist list. */
export const MAX_BRIEF_PANELIST_IDS = 100_000;
/** Fewer rows than this is a table in a brief, not a list. */
const MIN_IDS = 20;

export interface PanelistIdList {
    name: string;
    ids: string[];
}

const ID = /^\d{3,12}$/;
const HEADER = /panelist|^p\.?id$|^id$|respondent|member/i;
/** A column of zip codes is a location list (ooh/location-lists.ts), never a list of people. */
const ZIP_HEADER = /\bzip|postal|post ?code/i;
const ZIP_VALUE = /^\d{5}$/;

/**
 * The IDs in a sheet whose single column of values is panelist IDs: at least 20
 * rows, nine in ten of them whole numbers of 3 to 12 digits, with an optional
 * header. A sheet with other filled columns is a table, not a list.
 */
export function panelistIdsInSheet(sheet: Sheet, opts: { minIds?: number } = {}): string[] | null {
    const min = opts.minIds ?? MIN_IDS;
    const rows = sheet.rows
        .map((r) => r.map((c) => String(c ?? '').trim()))
        .filter((r) => r.some(Boolean));
    if (rows.length < min) return null;
    const filled = new Set(rows.flatMap((r) => r.flatMap((c, i) => (c ? [i] : []))));
    let col: number;
    if (filled.size === 1) col = [...filled][0];
    else {
        const header = rows[0].findIndex((c) => HEADER.test(c));
        if (header < 0) return null;
        col = header;
    }
    // A zip column is a zip list: its 5-digit numbers are not panelist IDs (UAT 2026-10-19:
    // a 300-zip file was also read as 300 panelists, which would have counted nobody).
    if (ZIP_HEADER.test(rows[0][col] ?? '')) return null;
    const body =
        HEADER.test(rows[0][col] ?? '') || !ID.test(rows[0][col] ?? '') ? rows.slice(1) : rows;
    const values = body.map((r) => r[col] ?? '').filter(Boolean);
    // No header, and nine in ten values are exactly five digits: zip codes, not IDs.
    if (
        !HEADER.test(rows[0][col] ?? '') &&
        values.filter((v) => ZIP_VALUE.test(v)).length >= values.length * 0.9
    )
        return null;
    const ids = values.filter((v) => ID.test(v));
    if (ids.length < min || ids.length < values.length * 0.9) return null;
    return [...new Set(ids)];
}

/** Every ID list in a workbook, one list named after the file. */
export function panelistIdsFromSheets(
    sheets: Sheet[],
    fileName: string,
): { list: PanelistIdList | null; idSheets: string[] } {
    const found = sheets.flatMap((s) => {
        const ids = panelistIdsInSheet(s);
        return ids ? [{ sheet: s.name, ids }] : [];
    });
    if (!found.length) return { list: null, idSheets: [] };
    const ids = [...new Set(found.flatMap((f) => f.ids))];
    return { list: { name: fileName.slice(0, 120), ids }, idSheets: found.map((f) => f.sheet) };
}

/** Several files' ID lists as one, named after them all. */
export function mergePanelistLists(lists: PanelistIdList[]): PanelistIdList | null {
    if (!lists.length) return null;
    return {
        name: lists
            .map((l) => l.name)
            .join(' + ')
            .slice(0, 120),
        ids: [...new Set(lists.flatMap((l) => l.ids))],
    };
}

/** The brief asks to leave the listed people out, rather than count only them. */
export function excludesList(text: string): boolean {
    return /\b(exclude|leave (?:them |these |those )?out|remove|suppress|do not (?:invite|contact)|lock ?out)\b[^.\n]{0,60}\b(list|ids?|panelists|file)\b/i.test(
        text,
    );
}

/**
 * The attachment's ID list on every draft's audience, as the form's upload
 * would put it, and DANI's questions and notes about a list it could not read
 * dropped. Over the limit, the list is left off and the draft says why.
 */
export function withPanelistList(
    drafts: BriefDraft[],
    list: PanelistIdList | null,
    text: string,
): BriefDraft[] {
    if (!list) return drafts;
    const aboutList = (t: string) =>
        /panelist id|list of (?:panelist|ids?)|id list|\.csv|\.xlsx/i.test(t);
    if (list.ids.length > MAX_BRIEF_PANELIST_IDS)
        return drafts.map((d) => ({
            ...d,
            questions: [
                `${list.name} has ${list.ids.length.toLocaleString('en-US')} panelist IDs; a check takes up to ${MAX_BRIEF_PANELIST_IDS.toLocaleString('en-US')}. Split it, or upload a shorter list in the form.`,
                ...d.questions.filter((q) => !aboutList(q)),
            ].slice(0, 5),
        }));
    const exclude = excludesList(text);
    return drafts.map((d) => {
        const panelists = { name: list.name, ids: list.ids, exclude };
        const has = d.request.criteria.some((c) => c.kind === 'demographic');
        const criteria: Criterion[] = has
            ? d.request.criteria.map((c) => (c.kind === 'demographic' ? { ...c, panelists } : c))
            : [{ kind: 'demographic', panelists }, ...d.request.criteria];
        const request = canonicalizeRequest(
            FeasibilityRequestSchema.parse({ ...d.request, criteria }),
        );
        return {
            ...d,
            request,
            questions: d.questions.filter((q) => !aboutList(q)),
            cannotTarget: d.cannotTarget.filter((c) => !aboutList(c)),
            sources: {
                ...d.sources,
                panelists: {
                    kind: 'brief',
                    passage:
                        `From the attachment: ${list.ids.length.toLocaleString('en-US')} panelist IDs in ${list.name}, ${exclude ? 'left out' : 'counted only'}`.slice(
                            0,
                            300,
                        ),
                },
            },
        };
    });
}
